'use client';

import { useState } from 'react';
import {
  LuPlay,
  LuX,
  LuMaximize,
  LuVolume2,
  LuVolumeX,
  LuChevronRight,
} from 'react-icons/lu';

// Define landscape video options with YouTube thumbnails
const videoOptions = [
  {
    id: 'main',
    title: 'Qwetulands: Transforming Spaces',
    description:
      'A comprehensive overview of our property solutions and services',
    youtubeId: 'boD8vekeGao', // Replace with your actual landscape video ID
    duration: '2:45',
  },
  {
    id: 'services',
    title: 'Our Professional Services',
    description: 'Detailed walkthrough of our premium service offerings',
    youtubeId: 'DSwDTUmmBvk', // Replace with actual ID
    duration: '3:16',
  },
];

// Function to get YouTube thumbnail URL (maxresdefault is highest quality)
const getYouTubeThumbnail = (
  videoId: string,
  quality: 'default' | 'hq' | 'mq' | 'sd' | 'maxres' = 'maxres'
) => {
  return `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`;
};

const VideoSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(videoOptions[0]);
  const [isHovering, setIsHovering] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<Record<string, boolean>>(
    {}
  );

  const openVideoModal = (video: (typeof videoOptions)[0]) => {
    setActiveVideo(video);
    setIsModalOpen(true);
  };

  // Handle thumbnail loading errors - fallback to standard quality if maxres fails
  const handleThumbnailError = (videoId: string) => {
    setThumbnailError((prev) => ({ ...prev, [videoId]: true }));
  };

  // Get the best available thumbnail quality
  const getThumbnailUrl = (videoId: string) => {
    return thumbnailError[videoId]
      ? getYouTubeThumbnail(videoId, 'hq')
      : getYouTubeThumbnail(videoId, 'maxres');
  };

  return (
    <section className='relative py-24 overflow-hidden'>
      {/* Background with gradient similar to other sections */}
      <div className='absolute inset-0 bg-linear-to-b from-gray-900 via-gray-900 to-gray-800' />

      {/* Grid Pattern - matching other sections */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMTIxMjEiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzBoMmwyLTJWMGgtMnYzMHoiLz48cGF0aCBkPSJNMzAgMGgydjJIMzB6TTAgMGgydjJIMHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-5" />

      {/* Animated Gradient Orbs - matching other sections */}
      <div className='absolute top-1/4 right-1/4 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl animate-float' />
      <div className='absolute bottom-1/4 left-1/4 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl animate-float-delayed' />
      <div className='absolute top-1/2 left-1/2 h-56 w-56 rounded-full bg-purple-500/20 blur-3xl animate-float-slow' />

      <div className='container relative mx-auto px-4 z-10'>
        {/* Section Header */}
        <div className='mx-auto mb-8 sm:mb-16 max-w-2xl text-center'>
          <h2 className='mb-3 sm:mb-4 text-2xl sm:text-3xl font-bold text-white lg:text-4xl'>
            <span className='relative inline-block'>
              <span className='absolute -inset-1 blur-xl bg-linear-to-r from-blue-400 via-purple-400 to-emerald-400 opacity-30' />
              <span className='relative'>
                Experience Our Services in Action
              </span>
            </span>
          </h2>
          <p className='text-base sm:text-lg text-gray-300 mb-4 sm:mb-8 px-2'>
            Watch how we transform properties and deliver excellence across our
            range of professional services.
          </p>
        </div>

        {/* Featured Video - Cinema-style Landscape Layout */}
        <div className='mx-auto max-w-5xl mb-10 sm:mb-20 px-2 sm:px-4'>
          <div
            className='relative aspect-video overflow-hidden rounded-lg sm:rounded-xl bg-black shadow-lg sm:shadow-2xl shadow-blue-500/10 ring-1 ring-white/10 group'
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Black Letterbox Bars for Cinema Feel */}
            <div className='absolute inset-x-0 top-0 h-[5%] sm:h-[7%] bg-black z-10'></div>
            <div className='absolute inset-x-0 bottom-0 h-[5%] sm:h-[7%] bg-black z-10'></div>

            {/* Thumbnail Image - Using YouTube thumbnail */}
            <img
              src={getThumbnailUrl(activeVideo.youtubeId)}
              alt={activeVideo.title}
              className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90'
              onError={() => handleThumbnailError(activeVideo.youtubeId)}
              loading="lazy"
            />

            {/* Overlay */}
            <div className='absolute inset-0 bg-linear-to-t from-black/80 via-black/40 to-transparent'>
              {/* Video Info - Bottom Left */}
              <div className='absolute bottom-6 sm:bottom-12 left-4 sm:left-8 max-w-[85%] sm:max-w-md z-20'>
                <div className='flex items-center space-x-2 mb-1'>
                  <div className='h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-linear-to-r from-red-500 to-pink-500 animate-pulse'></div>
                  <span className='text-xs sm:text-sm text-white/80 font-medium'>
                    FEATURED
                  </span>
                </div>
                <h3 className='text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2'>
                  {activeVideo.title}
                </h3>
                <p className='text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 line-clamp-2'>
                  {activeVideo.description}
                </p>
                <div className='flex items-center space-x-3 sm:space-x-4'>
                  <button
                    onClick={() => openVideoModal(activeVideo)}
                    className='px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-linear-to-r from-blue-500 to-purple-500 text-white text-sm sm:text-base font-medium flex items-center space-x-1 sm:space-x-2 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 w-auto'
                  >
                    <LuPlay className='h-4 w-4 sm:h-5 sm:w-5' />
                    <span>Watch Now</span>
                  </button>
                  <div className='text-white/70 text-xs sm:text-sm font-medium'>
                    {activeVideo.duration}
                  </div>
                </div>
              </div>

              {/* Video Controls - Bottom Right (appear on hover) - Hidden on mobile */}
              <div
                className={`absolute bottom-12 right-8 hidden sm:flex items-center space-x-4 transition-opacity duration-300 ${
                  isHovering ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <button className='h-10 w-10 rounded-full bg-white/10 backdrop-blur-xs flex items-center justify-center hover:bg-white/20 transition-all'>
                  <LuVolumeX className='h-5 w-5 text-white' />
                </button>
                <button className='h-10 w-10 rounded-full bg-white/10 backdrop-blur-xs flex items-center justify-center hover:bg-white/20 transition-all'>
                  <LuMaximize className='h-5 w-5 text-white' />
                </button>
              </div>

              {/* Play Button - Center */}
              <div className='absolute inset-0 flex items-center justify-center'>
                <button
                  onClick={() => openVideoModal(activeVideo)}
                  className='group flex h-16 w-16 sm:h-20 md:h-24 sm:w-20 md:w-24 items-center justify-center rounded-full bg-black/30 backdrop-blur-xs transition-all duration-300 hover:bg-black/50'
                >
                  <div className='flex h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 items-center justify-center rounded-full bg-linear-to-r from-blue-500 to-purple-500 text-white transition-all duration-300 group-hover:scale-110'>
                    <LuPlay className='h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 ml-1' />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Selection Row - Enhanced */}
        <div className='mx-auto max-w-5xl mb-10 sm:mb-20 px-2 sm:px-0'>
          <div className='flex items-center justify-between mb-4 sm:mb-6'>
            <h3 className='text-lg sm:text-xl font-semibold text-white'>More Videos</h3>
            <a
              href='#'
              className='text-blue-400 hover:text-blue-300 flex items-center text-sm sm:text-base transition-colors duration-200'
            >
              See all <span className="hidden sm:inline">videos</span> <LuChevronRight className='h-4 w-4 ml-1' />
            </a>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
            {videoOptions.map((video) => (
              <div
                key={video.id}
                className={`group relative cursor-pointer rounded-lg overflow-hidden aspect-video bg-gray-800 transition-all duration-300 ${
                  video.id === activeVideo.id
                    ? 'ring-2 ring-blue-500 scale-[1.02]'
                    : 'hover:ring-1 hover:ring-white/30'
                }`}
                onClick={() => setActiveVideo(video)}
              >
                {/* Cinema-style letterboxing */}
                <div className='absolute inset-x-0 top-0 h-[5%] sm:h-[7%] bg-black z-10'></div>
                <div className='absolute inset-x-0 bottom-0 h-[5%] sm:h-[7%] bg-black z-10'></div>

                {/* YouTube Thumbnail */}
                <img
                  src={getThumbnailUrl(video.youtubeId)}
                  alt={video.title}
                  className='h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-300'
                  onError={() => handleThumbnailError(video.youtubeId)}
                />
                <div className='absolute inset-0 bg-linear-to-t from-black/70 to-transparent flex flex-col justify-end p-4'>
                  <div className='relative z-20'>
                    <div className='flex justify-between items-center mb-1'>
                      <h4 className='text-white font-medium text-sm line-clamp-1'>
                        {video.title}
                      </h4>
                      <span className='text-white/70 text-xs'>
                        {video.duration}
                      </span>
                    </div>
                    <p className='text-gray-300 text-xs mt-1 line-clamp-1'>
                      {video.description}
                    </p>
                  </div>
                </div>
                {video.id === activeVideo.id && (
                  <div className='absolute top-2 right-2 bg-linear-to-r from-blue-500 to-purple-500 text-white text-xs py-1 px-2 rounded-full z-20'>
                    Now Playing
                  </div>
                )}
                <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                  <div className='h-12 w-12 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-xs'>
                    <LuPlay className='h-5 w-5 text-white ml-0.5' />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Points - redesigned to match overall theme */}
        <div className='mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3'>
          {[
            {
              title: 'Expertise',
              color: 'from-blue-500 to-blue-400',
              description:
                'Professional teams with years of industry experience',
            },
            {
              title: 'Innovation',
              color: 'from-purple-500 to-purple-400',
              description: 'Modern solutions using cutting-edge technology',
            },
            {
              title: 'Results',
              color: 'from-emerald-500 to-emerald-400',
              description: 'Proven track record of client satisfaction',
            },
          ].map((item, index) => (
            <div
              key={index}
              className='group rounded-xl bg-white/5 p-6 backdrop-blur-xs border border-white/10 transition-all duration-300 hover:bg-white/10'
            >
              <div
                className={`mb-3 text-2xl font-bold bg-linear-to-r ${item.color} bg-clip-text text-transparent`}
              >
                {item.title}
              </div>
              <p className='text-sm text-gray-300'>{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Video Modal - Cinematic Landscape Experience */}
      {isModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-6 md:p-8 backdrop-blur-xs'>
          <div className='relative w-full max-w-6xl rounded-xl overflow-hidden'>
            {/* Modal Header */}
            <div className='absolute top-0 left-0 right-0 flex justify-between items-center p-4 bg-linear-to-b from-black/80 to-transparent z-10'>
              <div>
                <h3 className='text-white font-bold'>{activeVideo.title}</h3>
                <p className='text-white/70 text-sm'>{activeVideo.duration}</p>
              </div>
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className='rounded-full bg-white/10 p-2 text-white transition-all hover:bg-white/20'
              >
                <LuX className='h-6 w-6' />
              </button>
            </div>

            {/* Video Player - Letterboxed for Cinema Feel */}
            <div className='aspect-video relative overflow-hidden bg-black'>
              <div className='absolute inset-x-0 top-0 h-[2%] sm:h-[3%] md:h-[5%] bg-black z-10'></div>
              <div className='absolute inset-x-0 bottom-0 h-[2%] sm:h-[3%] md:h-[5%] bg-black z-10'></div>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                className='h-full w-full absolute inset-0'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
                referrerPolicy='strict-origin-when-cross-origin'
              />
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float 6s ease-in-out 2s infinite;
        }

        .animate-float-slow {
          animation: float 8s ease-in-out 1s infinite;
        }
      `}</style>
    </section>
  );
};

export default VideoSection;
